import { useState, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { Card, Spin, Alert, Button, Typography, Space, Table, Tabs, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import eventsService from '../../api/eventsService';
import radarStatusService, {
  type RadarInputStatus,
  type RadarOutputStatus,
  type RadarTilesStatus,
  type RadarBucketFile,
} from '../../api/radarStatusService';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import './RadarStatusPage.css';

dayjs.extend(utc);

const GCS_CONSOLE_BROWSER = 'https://console.cloud.google.com/storage/browser';
const RADAR_BUCKET_URLS: Record<string, string> = {
  input: `${GCS_CONSOLE_BROWSER}/fia-radar-input-l2a-data`,
  output: `${GCS_CONSOLE_BROWSER}/fia-portal-nc`,
  tiles: `${GCS_CONSOLE_BROWSER}/fia-portal-tiles-2`,
};

const { Title, Text } = Typography;

function getFileTableColumns(bucket: string, showIngested?: boolean): ColumnsType<RadarBucketFile> {
  const columns: ColumnsType<RadarBucketFile> = [
    {
      title: 'File',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text code>{name}</Text>,
    },
    {
      title: 'Created (UTC)',
      dataIndex: 'timeCreated',
      key: 'timeCreated',
      width: 170,
      render: (t: string) => dayjs(t).utc().format('YYYY-MM-DD HH:mm:ss'),
    },
  ];
  if (showIngested) {
    columns.push({
      title: 'Ingested',
      dataIndex: 'ingested',
      key: 'ingested',
      width: 90,
      render: (_: unknown, record: RadarBucketFile) => {
        const isRealtime = !record.name.includes('+');
        if (!isRealtime) return '—';
        if (record.ingested === true) {
          return (
            <Tooltip title="radar_tiles table: Row exists">
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            </Tooltip>
          );
        }
        if (record.ingested === false) {
          return (
            <Tooltip title="radar_tiles table: No row yet">
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            </Tooltip>
          );
        }
        return '—';
      },
    });
  }
  columns.push({
    title: 'Path',
    dataIndex: 'path',
    key: 'path',
    ellipsis: true,
    render: (path: string) => (
      <Text code style={{ fontSize: 11 }}>
        gs://{bucket}/{path}
      </Text>
    ),
  });
  return columns;
}

/** Overall status summary - Input + Output (+ Tiles when event selected), no file lists */
interface OverallStatusProps {
  inputStatus: RadarInputStatus | null;
  outputStatus: RadarOutputStatus | null;
  tilesStatus: RadarTilesStatus | null;
  hasEvent: boolean;
  loading: boolean;
  error: string | null;
  tilesError: string | null;
  onRefresh: () => void;
}

function OverallStatus({
  inputStatus,
  outputStatus,
  tilesStatus,
  hasEvent,
  loading,
  error,
  tilesError,
  onRefresh,
}: OverallStatusProps) {
  const inputDelayed = hasEvent && (inputStatus?.isDelayed ?? false);
  const outputDelayed = hasEvent && (outputStatus?.isDelayed ?? false);
  const tilesDelayed = hasEvent && (tilesStatus?.isDelayed ?? false);
  const hasError = !!error || (hasEvent && !!tilesError);
  const anyDelayed = inputDelayed || outputDelayed || tilesDelayed;

  const columnStyle: CSSProperties = {
    minWidth: 0,
    maxHeight: 'calc(100vh - 220px)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  return (
    <Card
      title={
        <Space>
          <span>Status</span>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Space>
      }
      style={{
        borderColor: !hasEvent ? 'rgba(0,0,0,0.06)' : hasError ? '#ff4d4f' : anyDelayed ? '#ff7875' : '#52c41a',
        borderWidth: !hasEvent ? 1 : hasError || anyDelayed ? 2 : 1,
        width: '100%',
      }}
      styles={{
        body: {
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 24,
          maxHeight: 'calc(100vh - 220px)',
          overflow: 'hidden',
        },
      }}
    >
      {loading && !inputStatus && !outputStatus ? (
        <div style={{ textAlign: 'center', padding: 24, gridColumn: '1 / -1' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          title="Error loading status"
          description={error}
          style={{ gridColumn: '1 / -1' }}
        />
      ) : (
        <>
          {/* Column 1: Input (L2A) bucket */}
          <div style={columnStyle}>
            <Text strong style={{ display: 'block', marginBottom: 8, flexShrink: 0 }}>
              <a href={RADAR_BUCKET_URLS.input} target="_blank" rel="noopener noreferrer">Input (L2A) Bucket</a>
            </Text>
            {!hasEvent ? (
              <Alert type="info" title="No active event" description="Status is available when an event is currently active." />
            ) : inputStatus ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '12px 16px',
                    borderRadius: 8,
                    background: inputDelayed ? '#fff2f0' : '#f6ffed',
                    border: `1px solid ${inputDelayed ? '#ffccc7' : '#b7eb8f'}`,
                    flexShrink: 0,
                  }}
                >
                  {inputDelayed ? (
                    <ExclamationCircleOutlined style={{ color: '#cf1322', fontSize: 24 }} />
                  ) : (
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
                  )}
                  <div>
                    <Text strong={inputDelayed} type={inputDelayed ? 'danger' : undefined}>
                      {inputStatus.message}
                    </Text>
                    {inputStatus.latestFile && (
                      <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Latest: {inputStatus.latestFile}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <Alert type="info" title="No input data" />
            )}
          </div>

          {/* Column 2: Output (NetCDF) bucket */}
          <div style={columnStyle}>
            <Text strong style={{ display: 'block', marginBottom: 8, flexShrink: 0 }}>
              <a href={RADAR_BUCKET_URLS.output} target="_blank" rel="noopener noreferrer">Output (NetCDF) Bucket</a>
            </Text>
            {!hasEvent ? (
              <Alert type="info" title="No active event" description="Status is available when an event is currently active." />
            ) : outputStatus ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '12px 16px',
                    borderRadius: 8,
                    background: outputDelayed ? '#fff2f0' : '#f6ffed',
                    border: `1px solid ${outputDelayed ? '#ffccc7' : '#b7eb8f'}`,
                    flexShrink: 0,
                  }}
                >
                  {outputDelayed ? (
                    <ExclamationCircleOutlined style={{ color: '#cf1322', fontSize: 24 }} />
                  ) : (
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
                  )}
                  <div>
                    <Text strong={outputDelayed} type={outputDelayed ? 'danger' : undefined}>
                      {outputStatus.message}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Expected: {outputStatus.expectedFile}
                      </Text>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 0 }}>
                  <Space vertical size="small">
                    {outputStatus.files.map((f) => (
                      <div key={f.path} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {f.exists ? (
                          <Tooltip title="GCS file: Present in bucket">
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="GCS file: Missing from bucket">
                            <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                          </Tooltip>
                        )}
                        <Text code style={{ fontSize: 12 }}>{f.name}</Text>
                        <Text type="secondary">
                          {f.exists
                            ? `${dayjs(f.timeCreated!).utc().format('YYYY-MM-DD HH:mm:ss')} UTC`
                            : 'Missing'}
                        </Text>
                      </div>
                    ))}
                  </Space>
                </div>
              </>
            ) : (
              <Alert type="info" title="No output data" />
            )}
          </div>

          {/* Column 3: PNG Tiles bucket */}
          <div style={columnStyle}>
            <Text strong style={{ display: 'block', marginBottom: 8, flexShrink: 0 }}>
              <a href={RADAR_BUCKET_URLS.tiles} target="_blank" rel="noopener noreferrer">PNG Tiles Bucket</a>
            </Text>
            {!hasEvent ? (
              <Alert type="info" title="No active event" description="Status is available when an event is currently active." />
            ) : tilesError ? (
              <Alert type="error" title="Error loading tiles" description={tilesError} />
            ) : tilesStatus ? (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '12px 16px',
                    borderRadius: 8,
                    background: tilesDelayed ? '#fff2f0' : '#f6ffed',
                    border: `1px solid ${tilesDelayed ? '#ffccc7' : '#b7eb8f'}`,
                    flexShrink: 0,
                  }}
                >
                  {tilesDelayed ? (
                    <ExclamationCircleOutlined style={{ color: '#cf1322', fontSize: 24 }} />
                  ) : (
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
                  )}
                  <div>
                    <Text strong={tilesDelayed} type={tilesDelayed ? 'danger' : undefined}>
                      {tilesStatus.message}
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Expected: {tilesStatus.expectedRealtimeFile} + 6 nowcasting tiles
                      </Text>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 0 }}>
                  <Space vertical size="small">
                    {tilesStatus.files.map((f) => (
                      <div key={f.path} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {f.exists ? (
                          <Tooltip title="GCS file: Present in bucket">
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          </Tooltip>
                        ) : (
                          <Tooltip title="GCS file: Missing from bucket">
                            <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                          </Tooltip>
                        )}
                        <Text code style={{ fontSize: 12 }}>{f.name}</Text>
                        <Text type="secondary">
                          {f.exists
                            ? `${dayjs(f.timeCreated!).utc().format('YYYY-MM-DD HH:mm:ss')} UTC`
                            : 'Missing'}
                        </Text>
                        {f.type === 'realtime' && f.ingested === true && (
                          <Tooltip title="radar_tiles table: Row exists">
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                          </Tooltip>
                        )}
                        {f.type === 'realtime' && f.ingested === false && (
                          <Tooltip title="radar_tiles table: No row yet">
                            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 12 }} />
                          </Tooltip>
                        )}
                      </div>
                    ))}
                  </Space>
                </div>
              </>
            ) : (
              <Alert type="info" title="Loading tiles status…" />
            )}
          </div>
        </>
      )}
    </Card>
  );
}

/** Output bucket tab - 2-file status + latest files table */
interface OutputBucketTabProps {
  status: RadarOutputStatus | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function OutputBucketTab({ status, loading, error, onRefresh }: OutputBucketTabProps) {
  const isDelayed = status?.isDelayed ?? false;
  const hasError = !!error;

  return (
    <Card
      title={
        <Space>
          <a href={RADAR_BUCKET_URLS.output} target="_blank" rel="noopener noreferrer">Output (NetCDF) Bucket</a>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Space>
      }
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderColor: hasError ? '#ff4d4f' : isDelayed ? '#ff7875' : '#52c41a',
        borderWidth: hasError || isDelayed ? 2 : 1,
      }}
      styles={{
        body: {
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 24px',
        },
      }}
    >
      {loading && !status ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          title="Error loading status"
          description={error}
        />
      ) : status ? (
        <Space vertical size="middle" style={{ width: '100%', flex: 1, minHeight: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 16px',
              borderRadius: 8,
              background: isDelayed ? '#fff2f0' : '#f6ffed',
              border: `1px solid ${isDelayed ? '#ffccc7' : '#b7eb8f'}`,
              flexShrink: 0,
            }}
          >
            {isDelayed ? (
              <ExclamationCircleOutlined style={{ color: '#cf1322', fontSize: 24 }} />
            ) : (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
            )}
            <div>
              <Text strong={isDelayed} type={isDelayed ? 'danger' : undefined}>
                {status.message}
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Expected: {status.expectedFile}
                </Text>
              </div>
            </div>
          </div>

          <div style={{ flexShrink: 0 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Expected files status
            </Text>
            <Space vertical size="small">
              {status.files.map((f) => (
                <div key={f.path} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {f.exists ? (
                    <Tooltip title="GCS file: Present in bucket">
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title="GCS file: Missing from bucket">
                      <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                    </Tooltip>
                  )}
                  <Text code style={{ fontSize: 12 }}>{f.name}</Text>
                  <Text type="secondary">
                    {f.exists
                      ? `${dayjs(f.timeCreated!).utc().format('YYYY-MM-DD HH:mm:ss')} UTC`
                      : 'Missing'}
                  </Text>
                </div>
              ))}
            </Space>
          </div>

          {status.latestFiles.length > 0 && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Text strong style={{ marginBottom: 8 }}>
                Latest {status.latestFiles.length} files
              </Text>
              <Table
                size="small"
                dataSource={status.latestFiles}
                rowKey={(r) => r.path}
                columns={getFileTableColumns(status.bucket)}
                pagination={false}
                scroll={{ y: 'calc(100vh - 650px)' }}
              />
            </div>
          )}
        </Space>
      ) : (
        <Alert type="info" title="No data available" />
      )}
    </Card>
  );
}

/** PNG Tiles bucket tab - realtime + 6 nowcasting status + latest files table */
interface TilesBucketTabProps {
  status: RadarTilesStatus | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function TilesBucketTab({ status, loading, error, onRefresh }: TilesBucketTabProps) {
  const isDelayed = status?.isDelayed ?? false;
  const hasError = !!error;

  return (
    <Card
      title={
        <Space>
          <a href={RADAR_BUCKET_URLS.tiles} target="_blank" rel="noopener noreferrer">PNG Tiles Bucket</a>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Space>
      }
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderColor: hasError ? '#ff4d4f' : isDelayed ? '#ff7875' : '#52c41a',
        borderWidth: hasError || isDelayed ? 2 : 1,
      }}
      styles={{
        body: {
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 24px',
        },
      }}
    >
      {loading && !status ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          title="Error loading status"
          description={error}
        />
      ) : status ? (
        <Space vertical size="middle" style={{ width: '100%', flex: 1, minHeight: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 16px',
              borderRadius: 8,
              background: isDelayed ? '#fff2f0' : '#f6ffed',
              border: `1px solid ${isDelayed ? '#ffccc7' : '#b7eb8f'}`,
              flexShrink: 0,
            }}
          >
            {isDelayed ? (
              <ExclamationCircleOutlined style={{ color: '#cf1322', fontSize: 24 }} />
            ) : (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
            )}
            <div>
              <Text strong={isDelayed} type={isDelayed ? 'danger' : undefined}>
                {status.message}
              </Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Expected: {status.expectedRealtimeFile} + 6 nowcasting tiles
                </Text>
              </div>
            </div>
          </div>

          <div style={{ flexShrink: 0 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Expected files status
            </Text>
            <Space vertical size="small">
              {status.files.map((f) => (
                <div key={f.path} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {f.exists ? (
                    <Tooltip title="GCS file: Present in bucket">
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title="GCS file: Missing from bucket">
                      <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                    </Tooltip>
                  )}
                  <Text code style={{ fontSize: 12 }}>{f.name}</Text>
                  <Text type="secondary">
                    {f.exists
                      ? `${dayjs(f.timeCreated!).utc().format('YYYY-MM-DD HH:mm:ss')} UTC`
                      : 'Missing'}
                  </Text>
                  {f.type === 'realtime' && f.ingested === true && (
                    <Tooltip title="radar_tiles table: Row exists">
                      <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                    </Tooltip>
                  )}
                  {f.type === 'realtime' && f.ingested === false && (
                    <Tooltip title="radar_tiles table: No row yet">
                      <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 12 }} />
                    </Tooltip>
                  )}
                </div>
              ))}
            </Space>
          </div>

          {status.latestFiles.length > 0 && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Text strong style={{ marginBottom: 8 }}>
                Latest {status.latestFiles.length} files
              </Text>
              <Table
                size="small"
                dataSource={status.latestFiles}
                rowKey={(r) => r.path}
                columns={getFileTableColumns(status.bucket, true)}
                pagination={false}
                scroll={{ y: 'calc(100vh - 800px)' }}
              />
            </div>
          )}
        </Space>
      ) : (
        <Alert type="info" title="No data available" />
      )}
    </Card>
  );
}

/** Input bucket tab - status banner + latest file + files table (aligned with Output tab) */
interface InputBucketTabProps {
  status: RadarInputStatus | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function InputBucketTab({ status, loading, error, onRefresh }: InputBucketTabProps) {
  const isDelayed = status?.isDelayed ?? false;
  const hasError = !!error;

  return (
    <Card
      title={
        <Space>
          <a href={RADAR_BUCKET_URLS.input} target="_blank" rel="noopener noreferrer">Input (L2A) Bucket</a>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Space>
      }
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderColor: hasError ? '#ff4d4f' : isDelayed ? '#ff7875' : '#52c41a',
        borderWidth: hasError || isDelayed ? 2 : 1,
      }}
      styles={{
        body: {
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 24px',
        },
      }}
    >
      {loading && !status ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          title="Error loading status"
          description={error}
        />
      ) : status ? (
        <Space vertical size="middle" style={{ width: '100%', flex: 1, minHeight: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '12px 16px',
              borderRadius: 8,
              background: isDelayed ? '#fff2f0' : '#f6ffed',
              border: `1px solid ${isDelayed ? '#ffccc7' : '#b7eb8f'}`,
              flexShrink: 0,
            }}
          >
            {isDelayed ? (
              <ExclamationCircleOutlined style={{ color: '#cf1322', fontSize: 24 }} />
            ) : (
              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
            )}
            <div>
              <Text strong={isDelayed} type={isDelayed ? 'danger' : undefined}>
                {status.message}
              </Text>
              {status.latestFile && (
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Latest file: {status.latestFile}
                  </Text>
                </div>
              )}
            </div>
          </div>

          {status.latestFile && (
            <div style={{ flexShrink: 0 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Latest file status
              </Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {isDelayed ? (
                  <Tooltip title="GCS file: Delayed — File older than expected">
                    <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="GCS file: Present in bucket">
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </Tooltip>
                )}
                <Text code style={{ fontSize: 12 }}>{status.latestFile}</Text>
                <Text type="secondary">
                  {status.timeCreated
                    ? `${dayjs(status.timeCreated).utc().format('YYYY-MM-DD HH:mm:ss')} UTC`
                    : 'Unknown'}
                  {status.ageSeconds != null && (
                    <Text type="secondary"> ({status.ageSeconds}s ago)</Text>
                  )}
                </Text>
              </div>
            </div>
          )}

          {status.latestFiles.length > 0 && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <Text strong style={{ marginBottom: 8 }}>
                Latest {status.latestFiles.length} files
              </Text>
              <Table
                size="small"
                dataSource={status.latestFiles}
                rowKey={(r) => r.path}
                columns={getFileTableColumns(status.bucket)}
                pagination={false}
                scroll={{ y: 'calc(100vh - 600px)' }}
              />
            </div>
          )}
        </Space>
      ) : (
        <Alert type="info" title="No data available" />
      )}
    </Card>
  );
}

export const RadarStatusPage = () => {
  const { authState } = useAuth();
  const [inputStatus, setInputStatus] = useState<RadarInputStatus | null>(null);
  const [outputStatus, setOutputStatus] = useState<RadarOutputStatus | null>(null);
  const [tilesStatus, setTilesStatus] = useState<RadarTilesStatus | null>(null);
  const [activeEvent, setActiveEvent] = useState<{ season_year: number; circuit_id: string; start_date: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tilesError, setTilesError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    const token = authState.token;
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setTilesError(null);
    try {
      const inputPromise = radarStatusService.getInputStatus(token);
      const outputPromise = radarStatusService.getOutputStatus(token);
      const currentEventPromise = eventsService.getCurrentEvent();

      const [input, output, currentEvent] = await Promise.all([
        inputPromise,
        outputPromise,
        currentEventPromise,
      ]);
      setInputStatus(input);
      setOutputStatus(output);

      if (currentEvent) {
        setActiveEvent({
          season_year: currentEvent.season_year,
          circuit_id: currentEvent.circuit_id,
          start_date: currentEvent.start_date,
        });
        try {
          const tiles = await radarStatusService.getTilesStatus(
            token,
            currentEvent.season_year,
            currentEvent.circuit_id,
            currentEvent.start_date
          );
          setTilesStatus(tiles);
          setTilesError(null);
        } catch (err) {
          setTilesError(err instanceof Error ? err.message : 'Failed to load tiles status');
          setTilesStatus(null);
        }
      } else {
        setActiveEvent(null);
        setTilesStatus(null);
        setTilesError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status');
      setInputStatus(null);
      setOutputStatus(null);
      setTilesStatus(null);
      setActiveEvent(null);
    } finally {
      setLoading(false);
    }
  }, [authState.token]);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  return (
    <div
      style={{
        padding: 24,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        boxSizing: 'border-box',
      }}
    >
      <Title level={3} style={{ marginBottom: 8, flexShrink: 0 }}>
        Radar Status
      </Title>
      <p style={{ marginBottom: 16, color: 'rgba(0,0,0,0.65)', flexShrink: 0 }}>
        Monitor the status of radar data files being pushed to GCS buckets. Files are expected to
        arrive within a few seconds of the minute in the file name.
      </p>

      <Tabs
        defaultActiveKey="overall"
        className="radar-status-tabs"
        style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        items={[
          {
            key: 'overall',
            label: 'Overall Status',
            children: (
              <OverallStatus
                inputStatus={inputStatus}
                outputStatus={outputStatus}
                tilesStatus={tilesStatus}
                hasEvent={!!activeEvent}
                loading={loading}
                error={error}
                tilesError={tilesError}
                onRefresh={fetchStatus}
              />
            ),
          },
          {
            key: 'input',
            label: 'Input (L2A) Bucket',
            children: activeEvent ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <InputBucketTab
                  status={inputStatus}
                  loading={loading}
                  error={error}
                  onRefresh={fetchStatus}
                />
              </div>
            ) : (
              <Alert
                type="info"
                title="No active event"
                description="Status is available when an event is currently active (today falls between the event's start and end date)."
              />
            ),
          },
          {
            key: 'output',
            label: 'Output (NetCDF) Bucket',
            children: activeEvent ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <OutputBucketTab
                  status={outputStatus}
                  loading={loading}
                  error={error}
                  onRefresh={fetchStatus}
                />
              </div>
            ) : (
              <Alert
                type="info"
                title="No active event"
                description="Status is available when an event is currently active (today falls between the event's start and end date)."
              />
            ),
          },
          {
            key: 'png',
            label: 'PNG Tiles Bucket',
            children: activeEvent ? (
              <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <TilesBucketTab
                  status={tilesStatus}
                  loading={loading}
                  error={tilesError}
                  onRefresh={fetchStatus}
                />
              </div>
            ) : (
              <Alert
                type="info"
                title="No active event"
                description="Status is available when an event is currently active (today falls between the event's start and end date)."
              />
            ),
          },
        ]}
      />
    </div>
  );
};
